import React, { useMemo } from 'react';
import { Modal, Pressable, Text, View, StyleSheet, useWindowDimensions } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../../styles/theme';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import { useTranslation } from 'react-i18next';

type MenuKey = 'recipe' | 'dining' | 'leftover' | 'note' | 'skip' | 'clear';

interface Anchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MenuItem {
  key: MenuKey;
  labelKey: string;
  subKey?: string;
  tint: string;
  color: string;
  entryType?: 'dining' | 'leftover' | 'note' | 'skip';
}

interface Props {
  anchor: Anchor;
  hasContent: boolean;
  onPick: (key: MenuKey) => void;
  onClose: () => void;
}

const MENU_ITEMS: MenuItem[] = [
  { key: 'recipe',   labelKey: 'entry.chooseRecipe',   subKey: 'entry.subLibrary',    tint: Paprika.brandTint,        color: Paprika.primary },
  { key: 'dining',   labelKey: 'entry.diningLong',     subKey: 'entry.subEatingOut',  tint: ENTRY_META.dining.tint,   color: ENTRY_META.dining.color,   entryType: 'dining' },
  { key: 'leftover', labelKey: 'entry.leftovers',      subKey: 'entry.subRecentMeal', tint: ENTRY_META.leftover.tint, color: ENTRY_META.leftover.color, entryType: 'leftover' },
  { key: 'note',     labelKey: 'entry.noteFree',       subKey: 'entry.subNoteHint',   tint: ENTRY_META.note.tint,     color: ENTRY_META.note.color,     entryType: 'note' },
  { key: 'skip',     labelKey: 'entry.nothingThatDay', subKey: 'entry.subNoPlan',     tint: ENTRY_META.skip.tint,     color: ENTRY_META.skip.color,     entryType: 'skip' },
];

const CLEAR_ITEM: MenuItem = {
  key: 'clear',
  labelKey: 'entry.clearSlot',
  tint: Paprika.bgSunk,
  color: Paprika.textSecondary,
};

export function SlotMenu({ anchor, hasContent, onPick, onClose }: Props) {
  const { t } = useTranslation();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  const menuItems = useMemo(() => {
    const items = MENU_ITEMS;
    if (hasContent) {
      return [...items, CLEAR_ITEM];
    }
    return items;
  }, [hasContent]);

  const menuLeft = Math.min(Math.max(12, anchor.x), windowWidth - 262);
  const menuTop = Math.min(anchor.y + anchor.height + 6, windowHeight - 404);

  const headerCaption = hasContent ? t('entry.editSlotCaption') : t('entry.addToSlotCaption');

  return (
    <Modal visible={true} transparent={true} onRequestClose={onClose} animationType="none">
      <Pressable style={styles.backdrop} onPress={onClose} />
      <View style={[styles.menu, { left: menuLeft, top: menuTop }]}>
        <Text style={styles.header}>{headerCaption}</Text>
        <View style={styles.divider} />
        {menuItems.map((item, idx) => (
          <Pressable
            key={item.key}
            style={[
              styles.item,
              item.key === 'clear' && styles.itemClear,
              idx > 0 && styles.itemSeparator,
            ]}
            onPress={() => onPick(item.key)}
            testID={`slot-menu-${item.key}`}
          >
            <View style={[styles.iconBox, { backgroundColor: item.tint }]}>
              {item.entryType ? (
                <EntryIcon type={item.entryType} size={16} color={item.color} />
              ) : (
                <Feather name={item.key === 'recipe' ? 'book-open' : 'x'} size={16} color={item.color} />
              )}
            </View>
            <View style={styles.itemText}>
              <Text style={styles.itemLabel}>{t(item.labelKey)}</Text>
              {!!item.subKey && <Text style={styles.itemSub}>{t(item.subKey)}</Text>}
            </View>
          </Pressable>
        ))}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  menu: {
    position: 'absolute',
    width: 250,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 16,
    paddingVertical: 12,
    shadowColor: 'rgba(82, 68, 60, 0.12)',
    shadowOpacity: 1,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
  },
  header: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  divider: {
    height: 1,
    backgroundColor: Paprika.border,
    marginVertical: 8,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    minHeight: 34,
  },
  itemSeparator: {
    borderTopWidth: 1,
    borderTopColor: Paprika.border,
    marginTop: 8,
    paddingTop: 8,
  },
  itemClear: {
    borderTopWidth: 1,
    borderTopColor: Paprika.border,
    marginTop: 8,
    paddingTop: 8,
  },
  iconBox: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemText: {
    flex: 1,
    gap: 2,
  },
  itemLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.textPrimary,
  },
  itemSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
});
